import { useId } from 'react';
import { CalendarDays, ChevronDown, X } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ENTITY_CHIP_LABEL, type Chip, type SearchEntity } from '@/lib/search/searchFilterChips';
import { cn } from '@/lib/utils';

const FOCUS = 'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#8FB560]';
/** What an AI chip is, and what changing it does (T-151): the tooltip, and the controls' description. */
const AI_NOTE = 'Read by AI from your words. Removing it, or changing any filter, goes back to the plain reading.';

interface ChipsProps {
  chips: Chip[];
  entity?: SearchEntity;
  /** The entities the entity chip's menu offers (what App can search for this user). */
  entityOptions: readonly SearchEntity[];
  onRemove: (chip: Chip) => void;
  onEntity: (entity: SearchEntity) => void;
  /** Open the date picker; `opener` gets focus back when it closes. */
  onDateOpen: (opener: HTMLElement) => void;
  dateOpen: boolean;
}

/**
 * What the search understood, as removable chips (spec: nothing is hidden or
 * guessed silently). The entity chip is a menu; the date chip opens the
 * calendar. Existing tokens only: a subtle bordered pill, green only on focus.
 * A chip an AI reading filled (T-151) says so with a small muted "AI" label.
 */
export function SearchFilterChips({ chips, entity, entityOptions, onRemove, onEntity, onDateOpen, dateOpen }: ChipsProps) {
  const noteBase = useId();
  if (chips.length === 0) return null;
  return (
    <div role="list" aria-label="Search filters" className="flex flex-wrap items-center gap-1.5 border-b border-border px-3 py-2">
      {chips.map((chip, i) => {
        // An AI chip's controls say what it is and that changing it drops the whole reading.
        const noteId = chip.ai ? `${noteBase}-ai-${i}` : undefined;
        return (
        <div
          key={chip.key}
          role="listitem"
          title={chip.ai ? AI_NOTE : undefined}
          className="inline-flex h-6 items-center gap-0.5 whitespace-nowrap rounded-md border border-border pl-2 pr-0.5 text-xs text-foreground"
        >
          {chip.ai && (
            <span className="mr-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              AI<span className="sr-only"> reading:</span>
            </span>
          )}
          {/* hidden, not sr-only: aria-describedby still reads it, but it isn't read again while browsing, once per chip */}
          {noteId && <span id={noteId} hidden>{AI_NOTE}</span>}
          {chip.kind === 'entity' ? (
            <DropdownMenu>
              <DropdownMenuTrigger
                aria-label={`Type: ${chip.label}. Change type`}
                aria-describedby={noteId}
                className={cn('inline-flex items-center gap-1 rounded-sm', FOCUS)}
              >
                {chip.label}
                <ChevronDown className="h-3 w-3 text-muted-foreground" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuRadioGroup value={entity} onValueChange={(v) => onEntity(v as SearchEntity)}>
                  {entityOptions.map((e) => (
                    <DropdownMenuRadioItem key={e} value={e}>{ENTITY_CHIP_LABEL[e]}</DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : chip.kind === 'date' ? (
            <button
              type="button"
              onClick={(e) => onDateOpen(e.currentTarget)}
              aria-haspopup="dialog"
              aria-expanded={dateOpen}
              aria-label={`${chip.label}. Change dates`}
              aria-describedby={noteId}
              className={cn('inline-flex items-center gap-1 rounded-sm', FOCUS)}
            >
              <CalendarDays className="h-3 w-3 text-muted-foreground" />
              {chip.label}
              <ChevronDown className="h-3 w-3 text-muted-foreground" />
            </button>
          ) : (
            <span>{chip.label}</span>
          )}
          <button
            type="button"
            aria-label={chip.ai ? `Remove ${chip.label} (drops the AI reading)` : `Remove ${chip.label}`}
            aria-describedby={noteId}
            onClick={() => onRemove(chip)}
            className={cn('rounded-sm p-0.5 text-muted-foreground hover:text-foreground', FOCUS)}
          >
            <X className="h-3 w-3" />
          </button>
        </div>
        );
      })}
    </div>
  );
}
